import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { formatPhoneNumber, maskPhoneNumber } from '../../../helpers';
import { handleUserRedirect } from '../../../utils/userRedirect';
import { roleClassFor, roleLabelFor } from '../roles';

import React, { useContext, useEffect, useRef, useState } from 'react';

import Context from '../../../utils/context';
import GlobalUserSwitcher from '../global_user_switcher';
import PatientSessionNavButtons from '../patient_session_nav_buttons';

import '../../../../../app/assets/stylesheets/shared/super_user_banner.css';
import PostConversationModal from '../../questionnaire/post_conversation_survey_modal/index_gated.jsx';

const BANNER_HEIGHT_VARIABLE = '--impersonation-banner-height';

const maskEmail = (email) => {
  if (!email || email.indexOf('@') === -1) return null;
  const at = email.lastIndexOf('@');
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const domainName = dot === -1 ? domain : domain.slice(0, dot);
  const tld = dot === -1 ? '' : domain.slice(dot);
  const maskSegment = (value) => {
    if (!value) return '•';
    return `${value.charAt(0)}••••`;
  };
  return `${maskSegment(local)}@${maskSegment(domainName)}${tld}`;
};

const RevealableValue = ({ label, value, maskedValue }) => {
  const [revealed, setRevealed] = useState(false);
  if (!value) return null;

  const hiddenValue = maskedValue || '••••';
  return (
    <div className='pp-super-user-identity-row'>
      <span className='pp-super-user-identity-label'>{label}</span>
      <span className='pp-super-user-identity-value'>{revealed ? value : hiddenValue}</span>
      <button
        type='button'
        className='pp-super-user-identity-action'
        aria-label={`${revealed ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
        onClick={() => setRevealed((open) => !open)}
      >
        {revealed ? 'hide' : 'reveal'}
      </button>
    </div>
  );
};

const StopSessionIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path d='M7 7L17 17M17 7L7 17' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' />
    <path
      d='M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z'
      stroke='currentColor'
      strokeWidth='2'
    />
  </svg>
);

const CaretDownIcon = () => (
  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path d='M6 9L12 15L18 9' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const ImpersonationBanner = () => {
  const context = useContext(Context);
  const history = useHistory();
  const wrapRef = useRef(null);
  const bannerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showPostSurveyModal, setShowPostSurveyModal] = useState(false);
  const [postSurveyPath, setPostSurveyPath] = useState(null);
  const [mentorFirstName, setMentorFirstName] = useState(null);

  const isImpersonating = Boolean(context.auth.isImpersonating && context.auth.isImpersonating());
  const isSuperUser = context.auth.isSuperUser && context.auth.isSuperUser();
  const showBanner = Boolean(isSuperUser);

  // Full-height layouts (conversation room, settings) size themselves from the
  // viewport height, so they need to know how much space the banner occupies.
  useEffect(() => {
    if (!showBanner) return undefined;

    const root = document.documentElement;
    const updateHeight = () => {
      if (bannerRef.current) {
        root.style.setProperty(BANNER_HEIGHT_VARIABLE, `${bannerRef.current.offsetHeight}px`);
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      observer.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VARIABLE);
    };
  }, [showBanner]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClick = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      setMenuOpen(false);
      setSwitcherOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setSwitcherOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  if (!showBanner) return null;

  const currentUser = context.auth.getCurrentUser();
  const trueUser = isImpersonating ? currentUser.true_user : currentUser;
  const accountName = isImpersonating ? currentUser.account?.name || currentUser.account_name : null;
  const accountSlug = isImpersonating ? currentUser.account?.slug : null;
  const accountAdminPath = accountSlug ? `/admin/accounts/${accountSlug}` : null;
  const userName = currentUser.name || currentUser.email;
  const userAdminPath = `/admin/users/${currentUser.id}`;
  const trueUserName = trueUser?.name || trueUser?.email;
  const trueUserRoleLabel = roleLabelFor(trueUser?.current_profile_type);
  const trueUserRoleClass = roleClassFor(trueUser?.current_profile_type);
  const roleLabel = roleLabelFor(currentUser.current_profile_type);
  const roleClass = roleClassFor(currentUser.current_profile_type);

  const closeMenu = () => {
    setMenuOpen(false);
    setSwitcherOpen(false);
  };

  const openSurveyModal = ({ path, mentorName }) => {
    setPostSurveyPath(path || null);
    setMentorFirstName(mentorName || null);
    setShowPostSurveyModal(true);
    closeMenu();
  };

  const handleSwitchUser = (user) => {
    closeMenu();
    const start = () => {
      context.auth.impersonate(
        user.id,
        (impersonatedUser) => {
          handleUserRedirect(impersonatedUser, history, { isImpersonation: true });
        },
        (error) => console.error('Failed to impersonate user:', error)
      );
    };

    if (isImpersonating) {
      context.auth.stopImpersonating(start, (error) => console.error('Failed to stop impersonation:', error));
    } else {
      start();
    }
  };

  const handleStopSession = () => {
    context.auth.stopImpersonating(
      () => {
        window.location.href = '/admin';
      },
      () => {
        window.location.href = '/admin';
      }
    );
  };

  // Portal the bar onto the body. Ancestors of .App can become the containing block for
  // position:fixed (transforms, filters), which lets the bar scroll away while the
  // site header stays pinned under --impersonation-banner-height.
  const bar = (
    <div ref={wrapRef} id='pp-super-user-shell'>
      <div
        ref={bannerRef}
        className={`pp-super-user-banner ${isImpersonating ? 'pp-is-impersonating' : ''}`}
        role='status'
        data-testid='impersonation-banner'
      >
        <div className='pp-super-user-info'>
          <span className='pp-super-user-state'>Admin session</span>
          {isImpersonating && (
            <span className='pp-super-user-account' title={`Account: ${accountName || 'No account'}`}>
              {accountName || 'No account'}
            </span>
          )}
        </div>

        <div className='pp-super-user-actions'>
          <button
            type='button'
            className='pp-super-user-trigger'
            onClick={() =>
              setMenuOpen((open) => {
                if (open) setSwitcherOpen(false);
                return !open;
              })
            }
            aria-expanded={menuOpen}
            aria-haspopup='true'
            aria-label='Actions'
          >
            <span>Actions</span>
            <CaretDownIcon />
          </button>

          <a className='pp-super-user-admin' href='/admin'>
            Admin
          </a>

          {isImpersonating && (
            <button
              type='button'
              className='pp-super-user-stop'
              onClick={handleStopSession}
              title='Stop session'
              aria-label='Stop session'
            >
              <StopSessionIcon size={12} />
              <span>Stop session</span>
            </button>
          )}
        </div>
      </div>

      {menuOpen && (
        <div className='pp-super-user-menu' aria-label='Admin session actions'>
          <div className='pp-super-user-summary'>
            <span className='pp-super-user-summary-label'>Signed in as</span>
            <span className='pp-super-user-summary-row'>
              <strong className='pp-super-user-summary-name'>{trueUserName}</strong>
              <span className={`pp-super-user-role ${trueUserRoleClass}`}>{trueUserRoleLabel}</span>
            </span>
            {isImpersonating && (
              <div className='pp-super-user-impersonating'>
                <span className='pp-super-user-summary-label'>Impersonating</span>
                <div className='pp-super-user-identity-card'>
                  {Boolean(accountName) && (
                    <div className='pp-super-user-identity-row'>
                      <span className='pp-super-user-identity-label'>Account</span>
                      <span className='pp-super-user-identity-value pp-super-user-identity-account'>{accountName}</span>
                      {accountAdminPath && (
                        <a
                          className='pp-super-user-identity-action'
                          href={accountAdminPath}
                          aria-label='View account in admin'
                        >
                          view in admin
                        </a>
                      )}
                    </div>
                  )}
                  <div className='pp-super-user-identity-row'>
                    <span className='pp-super-user-identity-label'>User</span>
                    <span className='pp-super-user-identity-value pp-super-user-identity-user'>
                      <span className='pp-super-user-identity-name'>{userName}</span>
                      <span className={`pp-super-user-role ${roleClass}`}>{roleLabel}</span>
                    </span>
                    <a className='pp-super-user-identity-action' href={userAdminPath} aria-label='View user in admin'>
                      view in admin
                    </a>
                  </div>
                  <RevealableValue label='Email' value={currentUser.email} maskedValue={maskEmail(currentUser.email)} />
                  <RevealableValue
                    label='Phone'
                    value={formatPhoneNumber(currentUser.phone) || currentUser.phone}
                    maskedValue={maskPhoneNumber(currentUser.phone)}
                  />
                </div>
              </div>
            )}
          </div>

          {!switcherOpen && (
            <div className='pp-super-user-menu-actions'>
              <p className='pp-super-user-menu-label'>Actions</p>
              <button type='button' className='pp-super-user-menu-action' onClick={() => setSwitcherOpen(true)}>
                <span className='pp-super-user-menu-action-icon'>⇄</span>
                <span>Switch user</span>
              </button>
              {isImpersonating && (
                <PatientSessionNavButtons
                  variant='menu'
                  onNavigate={closeMenu}
                  onOpenPostSurvey={(data) =>
                    openSurveyModal({ path: data?.postSurveyPath, mentorName: data?.mentorFirstName })
                  }
                  menuActionClassName='pp-super-user-menu-action'
                  menuActionIconClassName='pp-super-user-menu-action-icon'
                />
              )}
              <div className='pp-super-user-menu-divider' />
              <a className='pp-super-user-menu-action pp-super-user-logout' href='/admin/logout'>
                Log out
              </a>
            </div>
          )}

          {switcherOpen && (
            <div className='pp-super-user-switcher'>
              <button type='button' className='pp-super-user-switcher-back' onClick={() => setSwitcherOpen(false)}>
                ‹ Back to actions
              </button>
              <GlobalUserSwitcher currentUserId={currentUser.id} onSelect={handleSwitchUser} />
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className='pp-super-user-spacer' aria-hidden='true' />
      {createPortal(bar, document.body)}
      {showPostSurveyModal && postSurveyPath && (
        <PostConversationModal
          postConversationPath={postSurveyPath}
          mentorName={mentorFirstName}
          onDismiss={() => setShowPostSurveyModal(false)}
          onClose={() => setShowPostSurveyModal(false)}
        />
      )}
    </>
  );
};

export default ImpersonationBanner;
